import {useNavigate} from "react-router-dom";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Divider,
    IconButton,
    Stack,
    Switch
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TextField from "@mui/material/TextField";
import * as React from "react";
import {useState} from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useAllResourceCategories} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {useAllChildResources} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import Button from "@mui/material/Button";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useOrganizationApps} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useMarketplaceApps} from "crqlar-react-common/dist/utils/hooks/app/useMarketplaceApps";
import Property from "crqlar-react-common/dist/components/Property";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import ExternalInfoPreview from "../common/ExternalInfoPreview";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import AddChildResourceDialog from "./AddChildResourceDialog";
import {useHasGuestAppModule} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {RESOURCE_ELEMENT_DEFAULT_COLORS} from "../../common/uiElements/colors";
import {getResourceDisplayKind} from "../../common/uiElements/utils";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
    resourceData: UseEntityData<Resource>
    titleSection?: React.ReactNode
    updateField?: (field: keyof Resource, value: any) => void
    updateMetaField?: (field: string, value: any) => void
    updateChildMetaField?: (childResource: Resource, field: string, value: any) => void
    handleRestaurantAutoAssignChange?: (value: boolean) => void
    handleMakeAllBookable?: (value: boolean) => void
    handleShowSeatsAroundTable?: (value: boolean) => void
    currentChildResources?: Resource[]
}

const hasOwnMetaField = (resource: Pick<Resource, 'meta'> | undefined, field: string) =>
    Object.prototype.hasOwnProperty.call(resource?.meta ?? {}, field)

const ResourceDetailsForm = (props: Props) => {

    useTrackScreenVisit('system resource details')

    const {t} = useTranslation()
    const {hasGuestAppModule} = useHasGuestAppModule()
    const navigate = useNavigate()
    const categories = useAllResourceCategories()
    const sectors: Sector[] = useAllSectors()

    const resource = props.resourceData?.item
    const disabled = !!resource.managedExternally

    // isValid here needs a category too, so both mandatory controls get the error state
    const nameMissing = !disabled && isRequiredTextMissing(props.resourceData, resource.name)
    const categoryMissing = !disabled
        && !props.resourceData.loading
        && !props.resourceData.isMissing
        && !resource.categoryIds?.length
    const childResources = useAllChildResources(resource.id ? [resource?.id] : [])
    const currentChildResources = props.currentChildResources ?? childResources
    const childrenTotalCapacity = currentChildResources.reduce((acc, child) => acc + (child.capacity || 0), 0)

    const [addChildDialogOpen, setAddChildDialogOpen] = useState(false)

    const allApps = useMarketplaceApps()
    const marketPlaceId = allApps.find(x => x.sku === "reservations" && x.name.toLowerCase() === "restaurant")?.id
    const organizationApps = useOrganizationApps()
    const appId = organizationApps.find(x => x.appId === marketPlaceId)?.id

    const restaurantSector = sectors.find(s => s.sectorType === SectorType.RESTAURANT)
    const restaurantSectorId = resource.sectorIds?.find(x => x === restaurantSector?.id)
    const isRestaurant = !!restaurantSectorId
    const isParent = !resource.parentId
    const sectorsById = React.useMemo(() => {
        const values = new Map<string, Sector>()
        sectors.forEach(sector => {
            if (sector.id) values.set(sector.id, sector)
        })
        return values
    }, [sectors])
    const categoriesById = React.useMemo(() => {
        const values = new Map<string, ResourceCategory>()
        categories.forEach(category => {
            if (category.id) values.set(category.id, category)
        })
        return values
    }, [categories])
    const resourceDisplayKind = getResourceDisplayKind(resource, sectorsById, categoriesById)
    const resourceColor = resource.meta?.tagColor || RESOURCE_ELEMENT_DEFAULT_COLORS[resourceDisplayKind]
    const autoAssignExternally = resource.meta?.autoAssignExternally ?? false

    // Group categories by their sector for leveled dropdown
    const sectorMap = React.useMemo(() => {
        const m = new Map<string, string>()
        sectors.forEach(s => {
            if (s.id) m.set(s.id, s.name || t('Other'))
        })
        return m
    }, [sectors, t])

    const getCategoryGroup = React.useCallback((category: ResourceCategory): string => {
        const firstSectorId = category.sectorIds?.[0]
        return firstSectorId ? (sectorMap.get(firstSectorId) || t('Other')) : t('Other')
    }, [sectorMap, t])

    const sortedCategories = React.useMemo(() =>
            [...categories].sort((a, b) =>
                getCategoryGroup(a).localeCompare(getCategoryGroup(b)) ||
                (a.name || '').localeCompare(b.name || '')
            ),
        [categories, getCategoryGroup]
    )

    const handleCategoriesChange = (selected: ResourceCategory[]) => {
        const categoryIds = selected
            .map(category => category.id)
            .filter((id): id is string => !!id)
        const derivedSectorIds = Array.from(new Set(selected.flatMap(category => category.sectorIds || [])))
        const nextIsRestaurant = !!restaurantSector?.id && derivedSectorIds.includes(restaurantSector.id)
        const shouldApplyRestaurantDefaults = nextIsRestaurant && !isRestaurant
        const shouldDefaultCapacity = shouldApplyRestaurantDefaults && resource.calculateCapacityFromChildren == null && !!resource.id
        const shouldDefaultAutoAssign = shouldApplyRestaurantDefaults && !hasOwnMetaField(resource, 'autoAssignExternally')
        props.resourceData.setItem({
            ...resource,
            categoryIds,
            sectorIds: derivedSectorIds,
            ...(shouldDefaultCapacity && {calculateCapacityFromChildren: true}),
            ...(shouldDefaultAutoAssign && {
                meta: {
                    ...resource.meta,
                    autoAssignExternally: true,
                },
            }),
        })
    }

    const advancedSettingsSection = (
        <AdditionalProperties title={t('Advanced Settings')} variant={'outlined'}>

            <Property title={t('Data tags')}>
                <Form
                    formData={resource?.tags || {}}
                    onChange={(e) => props.updateField?.('tags', e.formData)}
                    validator={validator}
                    formContext={{submit: false}}
                    templates={{ButtonTemplates: {SubmitButton: () => <div/>}}}
                    schema={{
                        type: 'object',
                        additionalProperties: [{type: 'string'}],
                        properties: {}
                    } as any}
                    uiSchema={{}}
                />
            </Property>
        </AdditionalProperties>
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label for this resource in calendars, floor plans, and booking screens.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={resource.name || ''}
                        onChange={(e) => props.updateField?.('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                        disabled={disabled}
                    />
                </Property>

                <Property
                    title={t('Color')}
                    tooltip={t('Color used to identify this resource in calendar and reservation views.')}
                >
                    <NewColorPicker
                        value={resourceColor}
                        disabled={disabled}
                        isRequired
                        onChange={(newColor) => props.updateMetaField?.('tagColor', newColor)}
                    />
                </Property>

                <Property
                    title={t('Resource Category')}
                    tooltip={t('Categories control which sectors and services can use this resource.')}
                >
                    <Autocomplete<ResourceCategory, true>
                        id={'categories'}
                        multiple
                        options={sortedCategories}
                        groupBy={getCategoryGroup}
                        getOptionLabel={(option: ResourceCategory) => option.name || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterSelectedOptions
                        disableCloseOnSelect
                        value={(resource.categoryIds || [])
                            .map(id => categories.find(c => c.id === id))
                            .filter(Boolean) as ResourceCategory[]}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size={'small'}
                                fullWidth
                                error={categoryMissing}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: {minHeight: 50}
                                }}
                            />
                        )}
                        fullWidth
                        onChange={(_e, value) => handleCategoriesChange(value)}
                        disabled={disabled}
                    />
                </Property>

                <Property
                    title={t('Capacity')}
                    tooltip={t('Maximum number of guests this resource can hold. Restaurant parent resources can sync this from child seats.')}
                >
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        gap={1}
                    >
                        <CNumberField
                            value={resource.calculateCapacityFromChildren ? childrenTotalCapacity : (resource.capacity ?? 0)}
                            onChange={(value) => props.updateField?.('capacity', value ?? 0)}
                            min={0}
                            size={'small'}
                            disabled={disabled || resource.calculateCapacityFromChildren}
                            sx={{
                                width: {xs: '100%', sm: 200},
                                flexShrink: 0,
                            }}
                        />
                        {isRestaurant && isParent && (
                            <Stack direction={'row'} alignItems={'center'} gap={0.5} sx={{whiteSpace: 'nowrap'}}>
                                <Switch
                                    size={'small'}
                                    checked={resource.calculateCapacityFromChildren}
                                    onChange={(e) => props.updateField?.('calculateCapacityFromChildren', e.target.checked)}
                                    disabled={disabled}
                                />
                                <Typography variant={'caption'}>
                                    {t('Sync with table list (sum of all seats)')}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </Property>
            </Stack>

            {hasGuestAppModule && advancedSettingsSection}
        </Stack>
    )

    const bookingSettingsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Booking settings')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Auto assign when booked through booking engine')}
                    tooltip={t('Allows externally booked reservations to use this resource automatically instead of requiring manual assignment.')}
                    mobileLayout={'inline'}
                >
                    <Switch
                        checked={autoAssignExternally}
                        onChange={(e) => {
                            if (isRestaurant && isParent) {
                                props.handleRestaurantAutoAssignChange?.(e.target.checked)
                            } else {
                                props.updateMetaField?.('autoAssignExternally', e.target.checked)
                            }
                        }}
                        disabled={disabled}
                    />
                </Property>

                {resource.parentId && (
                    <Property
                        title={t('Bookable through widget')}
                        tooltip={t('Makes this child resource selectable or usable in the guest booking widget.')}
                        mobileLayout={'inline'}
                    >
                        <Switch
                            checked={resource?.meta?.bookableExternally ?? false}
                            onChange={(e) => props.updateMetaField?.('bookableExternally', e.target.checked)}
                            disabled={disabled}
                        />
                    </Property>
                )}
            </Stack>
        </Stack>
    )

    // Child resources section — accordion, closed by default
    const childResourcesSection = (isParent && isRestaurant) ? (
        <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{'&:before': {display: 'none'}}}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                sx={{backgroundColor: 'action.hover', px: 2, borderRadius: 'inherit'}}
            >
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} width={'100%'} mr={1}>
                    <Typography variant={'subtitle1'} fontWeight={500}>
                        {`${t('Child resources')} (${currentChildResources.length || 0})`}
                    </Typography>
                    <Button
                        variant={'outlined'}
                        size={'small'}
                        endIcon={<OpenInNewIcon/>}
                        onClick={(e) => {
                            e.stopPropagation()
                            const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`
                            navigate(
                                `/restaurant/floorplan/${appId}/${restaurantSectorId}/${resource.id}/edit?returnTo=${encodeURIComponent(returnTo)}`,
                            )
                        }}
                        disabled={disabled || !resource.id}
                    >
                        {t('Floorplan')}
                    </Button>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{p: 2}}>
                <Stack gap={1}>
                    {currentChildResources.map((childResource: Resource) => (
                        <Stack
                            key={childResource.id}
                            direction={'row'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            gap={2}
                        >
                            {/* Name + edit icon */}
                            <Stack direction={'row'} alignItems={'center'} gap={0.5} sx={{flex: '1 1 0', minWidth: 0}}>
                                <Typography noWrap>{childResource.name}</Typography>
                                <IconButton
                                    size={'small'}
                                    onClick={() => navigate(`/system/resources/${childResource.id}`)}
                                >
                                    <EditIcon fontSize={'small'}/>
                                </IconButton>
                            </Stack>

                            <Stack direction={'row'} alignItems={'center'} gap={0.5} sx={{flex: '0 0 auto'}}>
                                <Switch
                                    size={'small'}
                                    checked={childResource?.meta?.autoAssignExternally ?? false}
                                    onChange={(e) => props.updateChildMetaField?.(childResource, 'autoAssignExternally', e.target.checked)}
                                />
                                <Typography variant={'body2'}>{t('Auto assign')}</Typography>
                            </Stack>

                            <Stack direction={'row'} alignItems={'center'} gap={0.5} sx={{flex: '0 0 auto'}}>
                                <Switch
                                    size={'small'}
                                    checked={childResource?.meta?.bookableExternally ?? false}
                                    onChange={(e) => props.updateChildMetaField?.(childResource, 'bookableExternally', e.target.checked)}
                                />
                                <Typography variant={'body2'}>{t('Bookable through widget')}</Typography>
                            </Stack>
                        </Stack>
                    ))}

                    {!disabled && (
                        <Box mt={1}>
                            <Button
                                startIcon={<AddIcon/>}
                                variant={'outlined'}
                                size={'small'}
                                onClick={() => setAddChildDialogOpen(true)}
                                disabled={!resource.id}
                            >
                                {t('Add child resource')}
                            </Button>
                        </Box>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    ) : null

    const externalInfoSection = resource.externalInfo?.length ? (
        <ExternalInfoPreview
            externalInfo={resource.externalInfo}
            setExternalInfo={e => props.updateField?.('externalInfo', e)}
        />
    ) : null

    const dangerZoneSection = resource.id ? (
        <Stack gap={{xs: 2.5, sm: 2}}>
            {props.resourceData.canDelete && (
                <DangerZone
                    title={t('Delete resource')}
                    description={t('This permanently deletes the resource and its related settings. Existing reservations using it may stop working correctly. This action cannot be undone.')}
                    buttonLabel={t('Delete')}
                    loading={props.resourceData.saving}
                    disabled={disabled}
                    onDelete={() => props.resourceData.removeNow()}
                />
            )}
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {resource.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    ) : null

    return (
        <ScreenWrapper>
            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    gap: {xs: 5, sm: 6},
                    alignSelf: 'center',
                }}
            >
                {props.titleSection}
                {basicInfoSection}
                <Divider/>
                {bookingSettingsSection}
                {childResourcesSection && (
                    <>
                        <Divider/>
                        {childResourcesSection}
                    </>
                )}
                {externalInfoSection && (
                    <>
                        <Divider/>
                        {externalInfoSection}
                    </>
                )}
                {dangerZoneSection && (
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                )}
            </Stack>

            {addChildDialogOpen && resource.id && (
                <AddChildResourceDialog
                    open={addChildDialogOpen}
                    parentId={resource.id}
                    onClose={() => setAddChildDialogOpen(false)}
                />
            )}
        </ScreenWrapper>
    )
}

export default ResourceDetailsForm
